
import { Card, CardContent } from '@/components/ui/card';
import { Zap, Smartphone, CreditCard, Wifi, Settings } from 'lucide-react';

const MobileOfferingsSection = () => {
  const offerings = [
    {
      icon: Zap,
      title: 'T-Mobile Business Unlimited Plans',
      description: '5G ready with priority data for your business operations.',
      highlight: '5G Ready',
      color: 'from-pink-500 to-purple-600'
    },
    {
      icon: Smartphone,
      title: 'Voice + Data Lines for Teams',
      description: 'Scalable solutions for teams ranging from 1 to 100+ users.',
      highlight: '1-100+ Users',
      color: 'from-purple-500 to-blue-600'
    },
    {
      icon: CreditCard,
      title: 'Device Financing Available',
      description: 'Business accounts with financing for iPhones, Samsungs, tablets and more.',
      highlight: 'Financing Available',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      icon: Wifi,
      title: 'eSIM Support',
      description: 'Remote provisioning capabilities for modern business flexibility.',
      highlight: 'Remote Setup',
      color: 'from-cyan-500 to-green-600'
    },
    {
      icon: Settings,
      title: 'Business Add-ons',
      description: 'International data, hotspot access, and additional business features.',
      highlight: 'Full Features',
      color: 'from-green-500 to-pink-600'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Modern animated background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-100/20 via-purple-100/20 to-blue-100/20 animate-pulse" style={{animationDuration: '4s'}} />
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-pink-300 rounded-full opacity-30 animate-bounce"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 animate-fade-in">
            What We Offer
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto animate-fade-in" style={{animationDelay: '0.2s'}}>
            Comprehensive T-Mobile business solutions tailored to your organization's needs.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {offerings.map((offering, index) => (
            <Card 
              key={offering.title} 
              className="group hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 hover:rotate-1 border-0 bg-white/90 backdrop-blur-sm animate-fade-in overflow-hidden"
              style={{animationDelay: `${0.1 * (index + 1)}s`}}
            >
              <CardContent className="p-8 relative">
                {/* Animated background gradient */}
                <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl ${offering.color} opacity-10 rounded-bl-full transition-all duration-700 group-hover:w-full group-hover:h-full group-hover:opacity-5 group-hover:scale-110`} />
                
                {/* Floating icon */}
                <div className={`w-16 h-16 bg-gradient-to-r ${offering.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-125 group-hover:rotate-12 transition-all duration-500 shadow-lg relative z-10 group-hover:shadow-2xl`}>
                  <offering.icon className="h-8 w-8 text-white group-hover:animate-pulse" />
                </div>

                {/* Animated highlight badge */}
                <div className="mb-4 transform group-hover:scale-110 transition-all duration-300">
                  <span className={`inline-block px-4 py-2 text-xs font-bold bg-gradient-to-r ${offering.color} text-white rounded-full shadow-lg group-hover:shadow-xl animate-pulse`}>
                    {offering.highlight}
                  </span>
                </div>

                {/* Content with hover effects */}
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-pink-600 transition-all duration-300 group-hover:scale-105">
                  {offering.title}
                </h3>
                <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                  {offering.description}
                </p>

                {/* Hover sparkles */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-1 h-1 bg-pink-400 rounded-full animate-ping"
                      style={{
                        left: `${20 + i * 30}%`,
                        top: `${10 + i * 20}%`,
                        animationDelay: `${i * 0.2}s`
                      }}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MobileOfferingsSection;
